import React, { useState, useEffect, useCallback } from 'react';
import { CalendarIcon, ClockIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { authService } from '../../services/authService';
import { Campaign } from '../../services/campaignService';

interface ScheduledCampaign extends Campaign {
  segments?: string[];
  totalRecipients?: number;
}

interface CampaignSchedulerProps {
  onScheduleCampaign?: (campaign: Campaign) => void;
  onRescheduleCampaign?: (campaign: Campaign) => void;
}

const CampaignScheduler: React.FC<CampaignSchedulerProps> = ({ 
  onScheduleCampaign,
  onRescheduleCampaign 
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [scheduledCampaigns, setScheduledCampaigns] = useState<ScheduledCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const loadScheduledCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/campaigns?status=scheduled`, {
        headers: authService.getAuthHeaders()
      });

      if (response.ok) {
        const campaigns = await response.json();
        setScheduledCampaigns(campaigns);
      }
    } catch (error) {
      console.error('Error loading scheduled campaigns:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    loadScheduledCampaigns();
  }, [loadScheduledCampaigns]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days: (Date | null)[] = [];
    
    // Add empty days for the beginning of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const getCampaignsForDate = (date: Date) => {
    return scheduledCampaigns.filter(campaign => {
      if (!campaign.scheduled_for) return false;
      const campaignDate = new Date(campaign.scheduled_for);
      return campaignDate.getDate() === date.getDate() &&
             campaignDate.getMonth() === date.getMonth() &&
             campaignDate.getFullYear() === date.getFullYear();
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  const monthYear = currentDate.toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = getDaysInMonth(currentDate);

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2 text-gray-500" />
            Campaign Schedule
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
            </button>
            <span className="text-lg font-medium px-4">{monthYear}</span>
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <ChevronRightIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="mt-4">
            {/* Week day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="h-24" />;
                }

                const campaignsForDate = getCampaignsForDate(date);
                const dayIsPast = isPast(date);
                const dayIsToday = isToday(date);

                return (
                  <div
                    key={date.toISOString()}
                    className={`
                      h-24 border rounded-lg p-2 relative cursor-pointer transition-colors
                      ${dayIsToday ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}
                      ${dayIsPast ? 'bg-gray-50' : 'hover:bg-gray-50'}
                      ${selectedDate?.toDateString() === date.toDateString() ? 'ring-2 ring-indigo-500' : ''}
                    `}
                    onClick={() => setSelectedDate(date)}
                  >
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      {date.getDate()}
                    </div>
                    
                    {campaignsForDate.length > 0 && (
                      <div className="space-y-1">
                        {campaignsForDate.slice(0, 2).map((campaign, idx) => (
                          <div
                            key={campaign.id}
                            className="text-xs bg-purple-100 text-purple-800 px-1 py-0.5 rounded truncate"
                            title={`${campaign.name} - ${formatTime(campaign.scheduled_for!)}`}
                          >
                            <ClockIcon className="h-3 w-3 inline mr-1" />
                            {formatTime(campaign.scheduled_for!)}
                          </div>
                        ))}
                        {campaignsForDate.length > 2 && (
                          <div className="text-xs text-gray-500">
                            +{campaignsForDate.length - 2} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Selected date details */}
            {selectedDate && (
              <div className="mt-6 border-t pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Campaigns for {selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long',
                    month: 'long', 
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </h4>
                
                {getCampaignsForDate(selectedDate).length > 0 ? (
                  <div className="space-y-3">
                    {getCampaignsForDate(selectedDate).map(campaign => (
                      <div key={campaign.id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="text-sm font-medium text-gray-900">
                              {campaign.name}
                            </h5>
                            <p className="text-sm text-gray-500 mt-1">
                              <ClockIcon className="h-4 w-4 inline mr-1" />
                              {formatTime(campaign.scheduled_for!)}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {campaign.estimated_recipients} recipients across {campaign.target_segment_count || 1} segment(s)
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            {onRescheduleCampaign && !isPast(selectedDate) && (
                              <button
                                onClick={() => onRescheduleCampaign(campaign)}
                                className="text-xs text-indigo-600 hover:text-indigo-800"
                              >
                                Reschedule
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No campaigns scheduled for this date</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignScheduler;